import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { Printer, Gift, MapPin, Phone } from 'lucide-react';

const LandingPage = () => {
  const [settings, setSettings] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const catalogues = [
    {
      id: 'post_print',
      title: 'Post Print Catalogue',
      description: 'Browse our complete range of printer consumables, spare parts, and accessories',
      icon: Printer
    },
    {
      id: 'gift_items',
      title: 'Customize Gift Items',
      description: 'Explore our collection of customizable gift and promotional items',
      icon: Gift
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header with Logo */}
      <header className="bg-blue-950 py-5 shadow-md">
        <div className="container mx-auto px-4 flex flex-col items-center gap-2">
          {settings?.company_logo && (
            <div className="bg-white rounded-md px-5 py-2 shadow-sm">
              <img
                src={(() => {
                  const logo = settings.company_logo;
                  if (logo.startsWith('data:')) return logo;
                  if (logo.includes('/uploads/')) {
                    const filename = logo.split('/uploads/').pop();
                    return `${API.replace('/api', '')}/uploads/${filename}`;
                  }
                  return `${API.replace('/api', '')}/uploads/${logo}`;
                })()}
                alt="United Copier Logo"
                className="h-16 md:h-20 w-auto object-contain mx-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}
          <p className="text-blue-200 text-xs md:text-sm tracking-widest uppercase mt-1">
            All Solutions Under A Roof for Printers
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4 md:p-8">
        <div className="max-w-5xl w-full">
          {/* Title */}
          <div className="text-center mb-10 md:mb-14">
            <h1 className="text-3xl md:text-5xl font-bold text-slate-900 tracking-tight mb-4">
              Welcome to United Copier
            </h1>
            <div className="w-16 h-1 bg-blue-900 mx-auto mb-4 rounded-full"></div>
            <p className="text-lg md:text-xl text-slate-600">
              Choose a catalogue to explore our products
            </p>
          </div>

          {/* Catalogue Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
            {catalogues.map((catalogue) => {
              const IconComponent = catalogue.icon;
              return (
                <Card
                  key={catalogue.id}
                  onClick={() => navigate(`/catalogue/${catalogue.id}`)}
                  className="cursor-pointer overflow-hidden bg-white border border-slate-200 hover:border-blue-900 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl group"
                >
                  <CardContent className="p-0">
                    {/* Icon Section */}
                    <div className="bg-blue-950 group-hover:bg-blue-900 p-8 md:p-12 flex items-center justify-center transition-colors duration-300">
                      <div className="border-2 border-white/25 rounded-full p-6 md:p-8 group-hover:border-white/40 transition-all duration-300">
                        <IconComponent className="h-16 w-16 md:h-24 md:w-24 text-white" strokeWidth={1.25} />
                      </div>
                    </div>

                    {/* Text Section */}
                    <div className="p-6 md:p-8 text-center">
                      <h2 className="text-xl md:text-2xl font-bold mb-3 text-slate-900 tracking-tight">
                        {catalogue.title}
                      </h2>
                      <p className="text-slate-600 text-sm md:text-base">
                        {catalogue.description}
                      </p>

                      {/* Arrow indicator */}
                      <div className="mt-6 inline-flex items-center justify-center px-6 py-2.5 rounded-md bg-blue-900 group-hover:bg-blue-800 text-white text-sm font-semibold transition-all duration-300 shadow-sm">
                        Browse Catalogue
                        <svg className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Trust strip */}
          <div className="mt-10 md:mt-14 flex flex-col md:flex-row items-center justify-center gap-3 md:gap-8 text-sm text-slate-500">
            <span className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-blue-900" />
              Indore &bull; Bhopal &bull; Jabalpur
            </span>
            <span className="hidden md:inline text-slate-300">|</span>
            <span className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-blue-900" />
              8103349299
            </span>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-blue-950 text-white py-6">
        <div className="container mx-auto px-4 text-center">
          <p className="text-slate-300">
            United Copier - All Solutions Under A Roof for Printers
          </p>
          <p className="text-sm text-slate-400 mt-2">
            &copy; {new Date().getFullYear()} United Copier. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
